import { useState, type FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { isCoach } from '../auth/roles';
import { usePermissions } from '../auth/usePermissions';
import { useSession } from '../auth/useSession';
import { IntelligenceLauncher } from '../features/intelligence/IntelligenceLauncher';
import { canSearchClients } from '../lib/clientSearch';
import { useBusiness } from '../lib/useBusiness';
import { whoAmI } from '../lib/whoami';

const TODAY_FORMAT = new Intl.DateTimeFormat('es-PA', { weekday: 'long', day: 'numeric', month: 'long' });

/**
 * The top of the work area on a wide screen (Helios look): where you are,
 * what day it is, a way to find a client, and who is signed in. Every part
 * reuses something the panel already does; nothing here is a new feature.
 */
export function WorkspaceBar() {
  const { session } = useSession();
  const business = useBusiness();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const permissions = usePermissions();
  const who = whoAmI(session, business, permissions.profile);
  const canSearch = canSearchClients({ coach: isCoach(session), plan: business?.plan, can: permissions.can });
  const place = business?.neighborhood ? `Sede ${business.neighborhood}` : 'Sede principal';

  function handleSearch(event: FormEvent) {
    event.preventDefault();
    const buscar = query.trim();
    void navigate({ to: '/clientes', search: buscar ? { buscar } : {} });
  }

  return (
    <div className="workspace-bar">
      <span className="ws-chip">
        <PlaceGlyph />
        {place}
      </span>
      <span className="ws-chip ws-date">
        <DayGlyph />
        {TODAY_FORMAT.format(new Date())}
      </span>
      {canSearch ? (
        <form className="ws-search" role="search" onSubmit={handleSearch}>
          <SearchGlyph />
          <input
            type="search"
            aria-label="Buscar clientes"
            placeholder="Buscar clientes"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </form>
      ) : (
        <span className="ws-spacer" />
      )}
      <IntelligenceLauncher />
      <span className="ws-who">
        <span className="ws-avatar" aria-hidden="true">
          {who.initials}
        </span>
        <span className="ws-who-text">
          <span className="ws-who-name">{who.name}</span>
          <span className="ws-who-role">{who.roleWord}</span>
        </span>
      </span>
    </div>
  );
}

function PlaceGlyph() {
  return (
    <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
      <path d="M4 17V8l6-4 6 4v9M8 17v-5h4v5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function DayGlyph() {
  return (
    <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
      <rect x="3.5" y="4.5" width="13" height="12" rx="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M3.5 8.5h13M7 3v3M13 3v3" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function SearchGlyph() {
  return (
    <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
      <circle cx="9" cy="9" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="m13.2 13.2 3.3 3.3" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}
